import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useSystemBanner } from '../contexts/SystemBannerContext';

// OpsBanner — site-wide yellow strip for maintenance windows, unstability
// warnings, "we're rolling something out, hang tight" messages. Reads
// from SystemBannerContext which polls GET /system/banner (S3-backed;
// see admin-cli/banner.go for the operator workflow). Empty / inactive
// banner → nothing rendered.
//
// Renders optional countdown when `until` is set on the banner ("in
// 2h 15m") so users have a concrete "come back when" instead of just
// a vague apology. Countdown text drops off gracefully once the target
// timestamp passes — the operator is expected to clear the banner via
// admin-cli around then, but a stale banner without the countdown
// still reads sensibly.
//
// Failure is silent — see SystemBannerContext. Mounts at the top of
// the App so it sits above the nav on every route.
const OpsBanner: React.FC = () => {
  const banner = useSystemBanner();
  const [tick, setTick] = React.useState(0);

  // Re-tick every 30s so the countdown text moves without polling the
  // API more often than the context already does. Only start the timer
  // when `until` is set (idle in the common case = no banner).
  const until = banner.active ? banner.until : undefined;
  React.useEffect(() => {
    if (!until) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [until]);

  if (!banner.active || !banner.message) return null;

  const countdown = formatCountdown(banner.until, tick);

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-yellow-400 text-yellow-950 border-b border-yellow-500/40"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-2 flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="leading-snug">
          {banner.message}
          {countdown && <span className="ml-2 opacity-75">· {countdown}</span>}
        </span>
      </div>
    </div>
  );
};

// formatCountdown returns "in Xh Ym" style delta from now to `until`,
// or empty string when the target is absent / unparseable / already
// passed. `_tick` is only present in the signature to make the caller's
// useState-driven re-render explicit — the actual value is ignored.
const formatCountdown = (until: string | undefined, _tick: number): string => {
  if (!until) return '';
  const target = Date.parse(until);
  if (!Number.isFinite(target)) return '';
  const deltaMs = target - Date.now();
  if (deltaMs <= 0) return '';
  const totalMin = Math.round(deltaMs / 60_000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
};

export default OpsBanner;
